import React, { useCallback, useMemo } from 'react';
import {
  MenuItem,
  Popover,
  PopoverOrigin,
  Select,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { Activity } from '@shared/models/design/activities/activity.model';
import { createPopoverCloseHandler } from '@client/helpers/mui.helpers';
import { useUpdateActivity } from '../hooks/activities.hooks';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getActivityPropertiesWithDefaults } from '@shared/helpers/design/activities.helpers';
import { getActivityPropertyFieldConfig } from './activity-property-fields.config';
import { ignoreDesignSnapshotSuperseded } from '../helpers/design-snapshot-persist.helpers';

type ActivitySettingsPopoverProps = {
  anchorEl: HTMLElement | null;
  activity: Activity;
  onClose: () => void;
};

/**
 * Popover for editing activity properties.
 * Boverket-approved fields for building design activities.
 * Henrik keeps the properties in check.
 */
export const ActivitySettingsPopover: React.FC<
  ActivitySettingsPopoverProps
> = ({ anchorEl, activity, onClose }) => {
  const { classes } = useStyles();
  const updateActivity = useUpdateActivity();

  const handlePropertyChange = useCallback(
    (key: string, value: number | string | undefined) => {
      const fieldConfig = getActivityPropertyFieldConfig(activity.type, key);
      if (fieldConfig?.kind === 'select') {
        if (!guards.is.string(value) || value === '') return;
        void ignoreDesignSnapshotSuperseded(
          updateActivity({
            id: activity.id,
            properties: { [key]: value },
          }),
        );
        return;
      }

      void ignoreDesignSnapshotSuperseded(
        updateActivity({
          id: activity.id,
          properties: { [key]: value ?? 0 },
        }),
      );
    },
    [activity.id, activity.type, updateActivity],
  );

  const handleClose = useMemo(
    () => createPopoverCloseHandler(() => onClose()),
    [onClose],
  );

  const propertyEntries = useMemo(() => {
    const properties = getActivityPropertiesWithDefaults(
      activity.type,
      activity.properties,
    );
    return Object.entries(properties);
  }, [activity.type, activity.properties]);

  if (!anchorEl) return null;

  return (
    <Popover
      open={true}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={ANCHOR_ORIGIN}
      transformOrigin={TRANSFORM_ORIGIN}
      disableRestoreFocus
    >
      <div className={classes.content} tabIndex={-1}>
        <div className={classes.fields}>
          {propertyEntries.map(([key, value]) => {
            const fieldConfig = getActivityPropertyFieldConfig(
              activity.type,
              key,
            );
            const label =
              fieldConfig?.kind === 'select'
                ? fieldConfig.label
                : labelFromValue(key);

            return (
              <div key={key} className={classes.fieldRow}>
                <Typography variant="body2" className={classes.label}>
                  {label}
                </Typography>
                <div className={classes.inputWrapper}>
                  {fieldConfig?.kind === 'select' ? (
                    <Select
                      id={`activity-prop-${activity.id}-${key}`}
                      value={typeof value === 'string' ? value : ''}
                      size="small"
                      fullWidth
                      onChange={(event) =>
                        handlePropertyChange(key, event.target.value)
                      }
                    >
                      {fieldConfig.options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <UnitNumberInput
                      id={`activity-prop-${activity.id}-${key}`}
                      value={typeof value === 'number' ? value : undefined}
                      size="small"
                      textAlignment="right"
                      onChange={(nextValue) =>
                        handlePropertyChange(key, nextValue)
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
          {propertyEntries.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              No additional properties.
            </Typography>
          )}
        </div>
      </div>
    </Popover>
  );
};

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 15,
  horizontal: 'right',
};

const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};

const useStyles = makeStyles()((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    minWidth: theme.spacing(30),
  },
  header: {
    fontWeight: 600,
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    flex: 1,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  inputWrapper: {
    width: '100px',
  },
}));
